import Grid from "@material-ui/core/Grid"
import Container from "@material-ui/core/Container"
import React from "react"
import { makeStyles } from "@material-ui/core"
import { Helmet } from "react-helmet"
import { Button } from "@material-ui/core/index"
import nvcg from "../images/medicalAestheticsv2.png"
import warningNl from "../images/kijk-uit-waarschuwing.webp"
import warningEn from "../images/caution-warning-en.webp"
import FacebookIcon from "@material-ui/icons/Facebook"
import InstagramIcon from "@material-ui/icons/Instagram"
import { Link } from "gatsby"
import { injectIntl } from "gatsby-plugin-intl"
import CookieConsent from "react-cookie-consent"
import RestoreConsent from "./RestoreConsent"

const COLUMN = 320

// Not a width test: an iPad Pro is 1024-1366px and would read as desktop.
const TOUCH = "@media (hover: none) and (pointer: coarse)"

// The warning is the Rijksoverheid's own campaign artwork; the banner links back
// to their page on cosmetic procedures.
const WARNING_SOURCE =
  "https://www.rijksoverheid.nl/themas/familie-zorg-en-gezondheid/cosmetische-ingrepen"

// 8% matches the gutter the footer switches to when the columns stack.
const BANNER_AT_BOTTOM = {
  display: "block",
  width: "84%",
  maxWidth: 440,
  height: "auto",
  marginLeft: "8%",
  marginTop: "3em",
}

const useStyles = makeStyles((theme) => ({
  // The library sets its styles inline, hence !important.
  cookieButtons: {
    [theme.breakpoints.down("xs")]: {
      display: "flex",
      width: "100%",
      gap: 12,
      "& button": {
        flex: "1 1 0 !important",
        margin: "0 !important",
        padding: "14px 12px !important",
        fontSize: "16px !important",
      },
    },
  },
  cookieBanner: {
    [theme.breakpoints.down("xs")]: {
      minHeight: 300,
      alignContent: "center",
    },
  },
  footer: {
    color: "#fff",
    backgroundColor: "#0E0E0E",
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(5),
    paddingBottom: theme.spacing(6),
    [theme.breakpoints.up("sm")]: {
      paddingTop: theme.spacing(10),
      paddingBottom: theme.spacing(10),
    },
  },
  leftContainer: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: "transparent",
    justifyContent: "space-between",
    minHeight: "350px",
    minWidth: "176px",
    [theme.breakpoints.down("sm")]: {
      minHeight: "200px",
      marginTop: "24px",
      marginBottom: "24px",
      alignItems: "flex-start",
      paddingLeft: "8%",
    },
  },
  middleContainer: {
    textAlign: "center",
  },
  rightContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    [theme.breakpoints.down("sm")]: {
      display: "block",
      textAlign: "left !important",
    },
  },
  rightInner: {
    paddingRight: "8%",
    [theme.breakpoints.down("sm")]: {
      paddingRight: 0,
      paddingLeft: "8%",
      width: "100%",
      textAlign: "left !important",
      "& address": {
        textAlign: "left !important",
        display: "block",
      },
      "& span, & a": {
        textAlign: "left !important",
      },
    },
  },
  nvcgimg: {
    width: "95%",
    maxWidth: COLUMN,
    marginLeft: "5%",
  },
  warningBanner: {
    display: "block",
    "& img": { display: "block", width: "100%", height: "auto" },
    width: "95%",
    maxWidth: COLUMN,
    height: "auto",
    marginLeft: "5%",
    marginTop: "2em",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
    [TOUCH]: {
      display: "none",
    },
  },
  warningBannerMobile: {
    display: "none",
    "& img": { display: "block", width: "100%", height: "auto" },
    [theme.breakpoints.down("sm")]: { ...BANNER_AT_BOTTOM },
    [TOUCH]: { ...BANNER_AT_BOTTOM },
  },
  visuallyHidden: {
    position: "absolute",
    width: 1,
    height: 1,
    margin: -1,
    padding: 0,
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    whiteSpace: "nowrap",
    border: 0,
  },
  since: {
    width: "95%",
    maxWidth: COLUMN,
    marginLeft: "5%",
    // No <br/> here: as flex items they push the copyright below the icons.
    marginTop: "2.2em",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    [theme.breakpoints.down("xs")]: {
      // marginTop: "24px",
    },
  },
  bookNowButton: {
    color: "#fff!important",
    fontSize: "16px!important",
    fontWeight: "bold",
    backgroundColor: "#929B91",
    borderRadius: "40px!important",
    padding: "1.0em 4em!important",
    textDecoration: "none!important",
    "&:hover": {
      textDecoration: "none!important",
      opacity: "0.5!important",
      backgroundColor: "#929B91!important",
    },
    [theme.breakpoints.down("xs")]: {
      marginTop: "24px!important",
      marginBottom: "24px!important",
    },
  },
  address: {
    fontStyle: "normal",
    [theme.breakpoints.down("sm")]: {
      textAlign: "left !important",
      display: "block",
    },
  },
  contactBlock: {
    marginTop: "1.5em",
    marginBottom: "1.5em",
  },
  legalBlock: {
    marginTop: "1.5em",
  },
  iconContainer: {
    display: "flex",
    justifyContent: "center",
  },
  link: {
    color: "#929B91",
    textDecoration: "none",
    fontSize: "0-9rem",
  },
  linkTo: {
    // lightened from #565A56 (2.75:1 on #0E0E0E — failed WCAG) to ~5:1; same gray-green
    color: "#80857F",
    textDecoration: "none",
    fontSize: "0-9rem",
    // inline-block + vertical padding lifts the tap target to >=24px (WCAG target-size)
    display: "inline-block",
    padding: "5px 0",
  },
  social: {
    position: "relative",
    display: "inline-block",
    top: 4,
    marginLeft: 20,
  },
  icons: {
    color: "white",
    paddingLeft: 10,
  },
}))

const Footer = (props) => {
  const classes = useStyles()
  const { intl } = props
  const locale = intl.locale

  const warning =
    locale === "nl"
      ? {
          src: warningNl,
          width: 640,
          height: 114,
          text:
            "Kijk uit. Jezelf mooier maken kan lelijk uitpakken. " +
            "Een geslaagde ingreep begint bij een geschikte arts.",
        }
      : {
          src: warningEn,
          width: 640,
          height: 133,
          text:
            "Caution. Cosmetic procedures carry risk. " +
            "A good result starts with a good doctor.",
        }

  const getUrl = (locale, url) => {
    if (locale === "nl") {
      if (url === "") {
        return "/"
      } else {
        // No locale to url with nl
        return "/" + url + "/"
      }
    }
    return `/${locale}/${url}/`
  }

  return (
    <Container maxWidth="xl" componen="footer" className={classes.footer}>
      <Helmet>
        {/* ClinicMinds widget.js is loaded (and re-loaded per route) in
            gatsby-browser.js on browser idle / first interaction, so the
            third-party script never competes with the page's own rendering. */}
        {/* <script
          src="//static.leadpages.net/leadboxes/current/embed.js"
          async
          defer
        ></script> */}
        {/* Google ADS CONVERSION TRACKING with Consent Mode.
            The gtag.js library is lazy-loaded by the inline script below (on first
            interaction or browser idle) so its ~149KB download does not compete with
            the LCP hero image. Consent Mode defaults are still set SYNCHRONOUSLY here,
            before the library loads, so Consent Mode v2 ordering is fully preserved. */}
        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}

            // Consent Mode default (denied until user accepts) — must run before gtag.js
            gtag('consent', 'default', {
              'ad_storage': 'denied',
              'analytics_storage': 'denied',
              'ad_user_data': 'denied',
              'ad_personalization': 'denied'
            });

            gtag('js', new Date());
            gtag('config', 'AW-618149115');

            // Defer the gtag.js library download so it doesn't steal bandwidth from the
            // hero image. The consent default + config above are already queued in
            // dataLayer, so the library replays them in order whenever it loads.
            (function () {
              var loaded = false;
              function loadGtag() {
                if (loaded) return;
                loaded = true;
                var s = document.createElement('script');
                s.async = true;
                s.src = 'https://www.googletagmanager.com/gtag/js?id=AW-618149115';
                document.head.appendChild(s);
              }
              function scheduleIdle() {
                if ('requestIdleCallback' in window) {
                  requestIdleCallback(loadGtag, { timeout: 3500 });
                } else {
                  setTimeout(loadGtag, 2000);
                }
              }
              ['scroll', 'mousemove', 'touchstart', 'keydown'].forEach(function (evt) {
                window.addEventListener(evt, loadGtag, { once: true, passive: true });
              });
              if (document.readyState === 'complete') {
                scheduleIdle();
              } else {
                window.addEventListener('load', scheduleIdle);
              }
            })();
          `}
        </script>
      </Helmet>

      {/* ✅ Restore consent from cookie on every page load */}
      <RestoreConsent />

      {/* ✅ NEW: Add CookieConsent visible on all pages */}
      <CookieConsent
        location="bottom"
        buttonText="Accept all"
        ariaAcceptLabel="Accept all"
        cookieName="faceInstituteCookie"
        enableDeclineButton
        declineButtonText="Reject all"
        ariaDeclineLabel="Reject all"
        // acceptOnScroll={true}
        // acceptOnScrollPercentage={30}
        onAccept={() => {
          if (typeof window.gtag === "function") {
            window.gtag("consent", "update", {
              ad_storage: "granted",
              analytics_storage: "granted",
              ad_user_data: "granted",
              ad_personalization: "granted",
            })
            window.gtag("config", "AW-618149115")
          }
          // Google Analytics (GA4)
          // Previously used Google Analytics Universal UA-147733846-1
          const gaScript = document.createElement("script")
          gaScript.async = true
          gaScript.src =
            "https://www.googletagmanager.com/gtag/js?id=G-0BD7N5QGXS"
          document.head.appendChild(gaScript)

          gaScript.onload = () => {
            window.gtag("js", new Date())
            window.gtag("config", "G-0BD7N5QGXS")
          }

          // Facebook Pixel
          const fbScript = document.createElement("script")
          fbScript.innerHTML = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod ?
    n.callMethod.apply(n,arguments) : n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '2613862042264786');
    fbq('consent', 'grant');
    fbq('track', 'PageView');
  `
          document.head.appendChild(fbScript)
        }}
        onDecline={() => {
          if (typeof window.gtag === "function") {
            window.gtag("consent", "update", {
              ad_storage: "denied",
              analytics_storage: "denied",
              ad_user_data: "denied",
              ad_personalization: "denied",
            })
          }
        }}
        style={{
          background: "rgb(25,25,25,0.95)",
          justifyContent: "center",
          alignItems: "center",
          fontSize: 16,
          padding: "14px 20px calc(14px + env(safe-area-inset-bottom))",
          boxSizing: "border-box",
        }}
        contentStyle={{ flex: "0 1 600px", margin: "8px 0" }}
        containerClasses={`CookieConsent ${classes.cookieBanner}`}
        buttonWrapperClasses={classes.cookieButtons}
        buttonStyle={{
          color: "black",
          backgroundColor: "#73FF73",

          padding: "8px 22px",
          fontSize: "14px",
          fontWeight: "bold",
          borderRadius: "20px",
          marginRight: 40,
        }}
        declineButtonStyle={{
          backgroundColor: "#484948",
          color: "#fff",
          fontWeight: "bold",
          padding: "8px 22px",
          fontSize: "14px",
          borderRadius: "20px",
        }}
        expires={180}
      >
        This website uses cookies for analytics and advertising purposes. Please
        choose: <br />
        <span style={{ color: "#9a9a9a" }}>Read more here: </span>
        <Link style={{ color: "#9a9a9a" }} to="/privacy-policy">
          privacy policy
        </Link>
      </CookieConsent>
      <footer>
        <Grid container justify="space-evenly" spacing={4} alignItems="center">
          <Grid item xs={12} sm={12} md={4} className={classes.leftContainer}>
            <span style={{ visibility: "hidden" }}>
              NVCG GECERTIFICEERD ARTS | COSMETIC DOCTOR
            </span>
            <img
              src={nvcg}
              alt="NVCG gecertificeerd arts — Medical Aesthetics"
              className={classes.nvcgimg}
            ></img>
            <a
              href={WARNING_SOURCE}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.warningBanner}
            >
              <img
                src={warning.src}
                alt=""
                width={warning.width}
                height={warning.height}
                loading="lazy"
                decoding="async"
              />
              <span className={classes.visuallyHidden}>{warning.text}</span>
            </a>
            <div className={classes.since}>
              <span>&copy; {new Date().getFullYear()} FACE INSTITUTE</span>
              <div className={classes.social}>
                <span>
                  {" "}
                  <a
                    href="https://www.facebook.com/faceinstitute.nl"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Face Institute on Facebook"
                    className={classes.icons}
                  >
                    <FacebookIcon />
                  </a>
                </span>
                <span>
                  <a
                    href="https://www.instagram.com/faceinstitute.nl/"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Face Institute on Instagram"
                    className={classes.icons}
                  >
                    <InstagramIcon />
                  </a>
                </span>
              </div>
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={4} className={classes.middleContainer}>
            <Link
              to={getUrl(locale, "contact")}
              style={{ textDecoration: "none" }}
            >
              <Button className={classes.bookNowButton}>
                Book appointment
              </Button>
            </Link>
            {/* <Button
              className={classes.bookNowButton}
              data-leadbox-popup="Yk5iMa5rq33XJXFSgKG5ML"
              data-leadbox-domain="kooldigital.lpages.co"
            >
              Book appointment
            </Button> */}
            {/* <Button
              href="https://schedule.clinicminds.com/?clinic=a8f5f0f3-1e41-11e9-881b-0a599d16f52a"
              className="clinicminds-scheduler-button"
              style={{
                backgroundColor: "#929B91",
                textDecoration: "none",
                fontSize: "14px",
                padding: "14px 50px",
                marginTop: "40px",
              }}
            >
              Book appointment
            </Button> */}
          </Grid>
          <Grid item xs={12} sm={12} md={4} className={classes.rightContainer}>
            <div className={classes.rightInner}>
              <address className={classes.address}>
                <span className={classes.link}>Address Amsterdam</span>
                <br />
                Frederiksplein 47B
                <br />
                1017 XL Amsterdam
                <br />
                <br />
              </address>
              <address className={classes.address}>
                <span className={classes.link}>Address Bergen</span>
                <br />
                Dokter van Peltlaan 5
                <br />
                1861 KA Bergen
              </address>
              <div className={classes.contactBlock}>
                <span className={classes.link}>General Clinic Inquiries</span>
                <br />
                +31 (0) 85 029 0829
                <br />
                <br />
                <span className={classes.link}>Email</span>
                <br />
                info@faceinstitute.nl
              </div>
              <div className={classes.legalBlock}>
                <span className={classes.link}>
                  {locale === "nl" ? "Juridisch" : "Legal"}
                </span>
                <br />
                <Link
                  exact
                  className={classes.linkTo}
                  to={getUrl(locale, "privacy-policy")}
                >
                  Privacy Policy
                </Link>
                <br />
                <Link
                  exact
                  className={classes.linkTo}
                  to={getUrl(locale, "terms-and-conditions")}
                >
                  Terms & Conditions
                </Link>
                <br />
                <Link
                  exact
                  className={classes.linkTo}
                  to={getUrl(locale, "complaint-process")}
                >
                  Quality & Complaints
                </Link>
                {/* <Link
                  exact
                  className={classes.linkTo}
                  to={getUrl(locale, "terms-and-conditions")}
                >
                  Complainment process
                </Link> */}
                {/* &copy; 2022 FACE INSTITUTE */}
                {/* <br /> */}
                {/* AMSTERDAM<br/><br/> */}
              </div>
            </div>
          </Grid>
        </Grid>
        <a
          href={WARNING_SOURCE}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.warningBannerMobile}
        >
          <img
            src={warning.src}
            alt=""
            width={warning.width}
            height={warning.height}
            loading="lazy"
            decoding="async"
          />
          <span className={classes.visuallyHidden}>{warning.text}</span>
        </a>
      </footer>
    </Container>
  )
}

export default injectIntl(Footer)
